/**
 * Central plan entitlement checks — use from UI and API routes.
 * Storage limits and tier definitions live in planLimits.ts.
 */
import {
  normalizeTier,
  getPlanConfig,
  canCreateStoryType,
  validateFileForUpload,
  type PlanTier,
  type PlanConfig,
  type StoryType,
} from './planLimits';
import { resolveEffectivePlanTier, type BillingIdentityLink } from '@/lib/billing/billing-identity';
import type { BillingProfileFields } from '@/lib/billing/paid-access';

export type { PlanTier } from './planLimits';

export type PlanErrorCode = 'PLAN_REQUIRED' | 'LIMIT_REACHED';

export type RequiredPlan = 'verified' | 'legacy' | 'institution_starter' | 'institution_growth';

export type PlanFeatureKey =
  | 'journey_map'
  | 'geospatial_map'
  | 'family_ledger'
  | 'vault_analytics'
  | 'advanced_analytics'
  | 'verification'
  | 'credential_upload'
  | 'time_capsule'
  | 'voice_upload'
  | 'video_upload'
  | 'document_upload'
  | 'public_proof_cards'
  | 'institution_dashboard'
  | 'institution_story_ledger'
  | 'institution_verification_inbox'
  | 'institution_heritage_archive'
  | 'institution_geospatial'
  | 'institution_bulk_verification'
  | 'institution_api';

export type PlanCheckResult =
  | { ok: true }
  | {
      ok: false;
      code: PlanErrorCode;
      requiredPlan?: RequiredPlan;
      message: string;
    };

export interface PlanEntitlements {
  tier: PlanTier;
  maxRecords: number;
  maxStorageMb: number;
  canUseJourneyMap: boolean;
  canUseGeospatialMap: boolean;
  canUseFamilyLedger: boolean;
  canUseVaultAnalytics: boolean;
  canUseAdvancedAnalytics: boolean;
  canUploadCredentials: boolean;
  canRequestVerification: boolean;
  canUseTimeCapsule: boolean;
  canUploadVoice: boolean;
  canUploadVideo: boolean;
  canUploadDocuments: boolean;
  canUploadPhotos: boolean;
  canCreatePublicProofCards: boolean;
  canUseInstitutionDashboard: boolean;
  canUseInstitutionStoryLedger: boolean;
  canUseInstitutionVerificationInbox: boolean;
  canUseInstitutionHeritageArchive: boolean;
  canUseInstitutionGeospatial: boolean;
  canUseInstitutionBulkVerification: boolean;
  canUseInstitutionApi: boolean;
  maxInstitutionMembers: number;
  maxCredentialIssuance: number;
  verifiedBadge: boolean;
}

export function getEntitlements(tier: PlanTier): PlanEntitlements {
  const c = getPlanConfig(tier);
  return {
    tier: c.tier,
    maxRecords: c.maxRecords,
    maxStorageMb: Math.round(c.storageLimitBytes / (1024 * 1024)),
    canUseJourneyMap: c.canUseJourneyMap,
    canUseGeospatialMap: c.canUseGeospatial,
    canUseFamilyLedger: c.canUseFamilyLedger,
    canUseVaultAnalytics: c.canAccessAnalytics,
    canUseAdvancedAnalytics: c.canUseAdvancedAnalytics,
    canUploadCredentials: c.canUploadCredentials,
    canRequestVerification: c.canRequestVerification,
    canUseTimeCapsule: c.canUseTimeCapsule,
    canUploadVoice: c.canUploadVoice,
    canUploadVideo: c.canUploadVideo,
    canUploadDocuments: c.canUploadDocuments,
    canUploadPhotos: c.canUploadPhotos,
    canCreatePublicProofCards: c.canGenerateProofCards,
    canUseInstitutionDashboard: c.canUseInstitutionDashboard,
    canUseInstitutionStoryLedger: c.canUseStoryLedger,
    canUseInstitutionVerificationInbox: c.canUseVerificationInbox,
    canUseInstitutionHeritageArchive: c.canUseHeritageArchive,
    canUseInstitutionGeospatial: c.canUseGeospatial,
    canUseInstitutionBulkVerification: c.canUseBulkVerification,
    canUseInstitutionApi: c.canUseAPI,
    maxInstitutionMembers: c.maxSeats,
    maxCredentialIssuance: c.maxVerificationsPerMonth,
    verifiedBadge: c.verifiedBadge,
  };
}

export function planDenied(
  code: PlanErrorCode,
  message: string,
  requiredPlan?: RequiredPlan
): PlanCheckResult {
  return { ok: false, code, message, requiredPlan };
}

export function requiredPlanForFeature(feature: PlanFeatureKey): RequiredPlan {
  switch (feature) {
    case 'family_ledger':
    case 'geospatial_map':
    case 'advanced_analytics':
    case 'video_upload':
      return 'legacy';
    case 'institution_dashboard':
    case 'institution_story_ledger':
    case 'institution_verification_inbox':
    case 'institution_heritage_archive':
    case 'institution_geospatial':
      return 'institution_starter';
    case 'institution_bulk_verification':
    case 'institution_api':
      return 'institution_growth';
    default:
      return 'verified';
  }
}

export function featureLabel(feature: PlanFeatureKey): string {
  const labels: Record<PlanFeatureKey, string> = {
    journey_map: 'Journey Map',
    geospatial_map: 'Geospatial Map',
    family_ledger: 'Family Ledger',
    vault_analytics: 'Vault Analytics',
    advanced_analytics: 'Advanced Vault Analytics',
    verification: 'Verification requests',
    credential_upload: 'Credential evidence uploads',
    time_capsule: 'Time Capsule',
    voice_upload: 'Voice notes',
    video_upload: 'Video stories',
    document_upload: 'Document uploads',
    public_proof_cards: 'Public proof cards',
    institution_dashboard: 'Institution dashboard',
    institution_story_ledger: 'Institution Story Ledger',
    institution_verification_inbox: 'Institution verification inbox',
    institution_heritage_archive: 'Institution heritage archive',
    institution_geospatial: 'Institution geospatial tools',
    institution_bulk_verification: 'Bulk institution verification',
    institution_api: 'Institution API access',
  };
  return labels[feature] || feature;
}

export function featureDescription(feature: PlanFeatureKey): string {
  const copy: Record<PlanFeatureKey, string> = {
    journey_map:
      'Map your life milestones on a visual timeline and story path that grows with every memory you add.',
    geospatial_map:
      'See where your public records and stories connect on an interactive world map with hologram view.',
    family_ledger:
      'Build a shared family tree, deposit records, and preserve heritage across generations.',
    vault_analytics:
      'See who is viewing your public vault and records — views, audience, and growth over time.',
    advanced_analytics:
      'Country-level viewer breakdown, device insights, and deeper audience analytics for your vault.',
    verification:
      'Submit achievements for institutional verification and earn trusted proof on your vault.',
    credential_upload:
      'Upload certificates, diplomas, and credential evidence to support your verified records.',
    time_capsule:
      'Schedule messages and media to unlock in the future for yourself or your vault visitors.',
    voice_upload: 'Add voice notes and audio stories to your Story Hub.',
    video_upload: 'Publish video stories and rich media to your vault.',
    document_upload: 'Archive PDFs and documents alongside your stories and achievements.',
    public_proof_cards: 'Generate shareable proof cards for verified records.',
    institution_dashboard: 'Manage members, verification, and your institution public archive.',
    institution_story_ledger: 'Create and manage records in the institution Story Ledger.',
    institution_verification_inbox: 'Review and issue verified institution records.',
    institution_heritage_archive: 'Create collections and contribute heritage archive items.',
    institution_geospatial: 'Create geospatial institution records and experiences.',
    institution_bulk_verification: 'Run bulk institution verification operations.',
    institution_api: 'Use institution API and automation capabilities.',
  };
  return copy[feature] || 'Upgrade to unlock this feature.';
}

function featureAllowed(tier: PlanTier, feature: PlanFeatureKey): boolean {
  const e = getEntitlements(tier);
  switch (feature) {
    case 'journey_map':
      return e.canUseJourneyMap;
    case 'geospatial_map':
      return e.canUseGeospatialMap;
    case 'family_ledger':
      return e.canUseFamilyLedger;
    case 'vault_analytics':
      return e.canUseVaultAnalytics;
    case 'advanced_analytics':
      return e.canUseAdvancedAnalytics;
    case 'verification':
      return e.canRequestVerification;
    case 'credential_upload':
      return e.canUploadCredentials;
    case 'time_capsule':
      return e.canUseTimeCapsule;
    case 'voice_upload':
      return e.canUploadVoice;
    case 'video_upload':
      return e.canUploadVideo;
    case 'document_upload':
      return e.canUploadDocuments;
    case 'public_proof_cards':
      return e.canCreatePublicProofCards;
    case 'institution_dashboard':
      return e.canUseInstitutionDashboard;
    case 'institution_story_ledger':
      return e.canUseInstitutionStoryLedger;
    case 'institution_verification_inbox':
      return e.canUseInstitutionVerificationInbox;
    case 'institution_heritage_archive':
      return e.canUseInstitutionHeritageArchive;
    case 'institution_geospatial':
      return e.canUseInstitutionGeospatial;
    case 'institution_bulk_verification':
      return e.canUseInstitutionBulkVerification;
    case 'institution_api':
      return e.canUseInstitutionApi;
    default:
      return false;
  }
}

export function checkFeatureAccess(
  rawTier: string | null | undefined,
  feature: PlanFeatureKey
): PlanCheckResult {
  const tier = normalizeTier(rawTier);
  if (featureAllowed(tier, feature)) return { ok: true };
  const required = requiredPlanForFeature(feature);
  return planDenied(
    'PLAN_REQUIRED',
    `${featureLabel(feature)} requires the ${required === 'legacy' ? 'Legacy' : required === 'verified' ? 'Verified' : 'Institution'} plan.`,
    required
  );
}

export function canCreateRecord(plan: PlanTier, currentCount: number): boolean {
  const config = getPlanConfig(plan);
  if (config.maxRecords === -1) return true;
  return currentCount < config.maxRecords;
}

export function recordLimitMessage(plan: PlanTier): string {
  const config = getPlanConfig(plan);
  if (config.maxRecords === -1) return '';
  return `You've reached the ${config.displayName} limit of ${config.maxRecords} records. Upgrade or reduce usage to add another record.`;
}

export function checkRecordLimit(
  rawTier: string | null | undefined,
  currentCount: number
): PlanCheckResult {
  const tier = normalizeTier(rawTier);
  if (canCreateRecord(tier, currentCount)) return { ok: true };
  const config = getPlanConfig(tier);
  if (config.maxRecords !== -1 && currentCount >= config.maxRecords) {
    return planDenied(
      'LIMIT_REACHED',
      `You have ${currentCount} records but your ${config.displayName} plan allows ${config.maxRecords}. Existing records are preserved; upgrade or delete records to add more.`,
      tier === 'free' ? 'verified' : 'legacy'
    );
  }
  return planDenied('LIMIT_REACHED', recordLimitMessage(tier), 'verified');
}

export type PlanUsageSnapshot = {
  recordCount: number;
  storageUsedBytes: number;
};

export function getUsageLimitFlags(
  tier: PlanTier,
  usage: PlanUsageSnapshot
): {
  overRecordLimit: boolean;
  overStorageLimit: boolean;
  overPlanLimits: boolean;
  canCreateRecords: boolean;
  canUploadMoreMedia: boolean;
} {
  const config = getPlanConfig(tier);
  const overRecordLimit = config.maxRecords !== -1 && usage.recordCount > config.maxRecords;
  const overStorageLimit =
    config.storageLimitBytes > 0 && usage.storageUsedBytes > config.storageLimitBytes;
  const atRecordCap = config.maxRecords !== -1 && usage.recordCount >= config.maxRecords;
  const atStorageCap =
    config.storageLimitBytes > 0 && usage.storageUsedBytes >= config.storageLimitBytes;

  return {
    overRecordLimit,
    overStorageLimit,
    overPlanLimits: overRecordLimit || overStorageLimit,
    canCreateRecords: !atRecordCap,
    canUploadMoreMedia: !atStorageCap,
  };
}

export function checkStoryType(
  rawTier: string | null | undefined,
  storyType: StoryType
): PlanCheckResult {
  const tier = normalizeTier(rawTier);
  if (canCreateStoryType(tier, storyType)) return { ok: true };
  const required: RequiredPlan =
    storyType === 'video' ? 'legacy' : storyType === 'photo' ? 'verified' : 'verified';
  return planDenied(
    'PLAN_REQUIRED',
    storyType === 'written'
      ? 'Written stories are included on Free.'
      : `${storyType.replace('_', ' ')} requires an upgraded plan.`,
    required
  );
}

export function checkFileUpload(
  rawTier: string | null | undefined,
  file: { type: string; size: number; name?: string },
  storageUsedBytes: number
): PlanCheckResult {
  const tier = normalizeTier(rawTier);
  const blob = new File([], file.name || 'upload', { type: file.type });
  Object.defineProperty(blob, 'size', { value: file.size });
  const result = validateFileForUpload(blob, tier, storageUsedBytes);
  if (result.ok) return { ok: true };
  const needsLegacy = file.type.startsWith('video/');
  return planDenied(
    result.error?.includes('storage') ? 'LIMIT_REACHED' : 'PLAN_REQUIRED',
    result.error || 'Upload not allowed on your plan.',
    needsLegacy ? 'legacy' : 'verified'
  );
}

export function checkStorageQuota(
  rawTier: string | null | undefined,
  storageUsedBytes: number,
  additionalBytes: number
): PlanCheckResult {
  const tier = normalizeTier(rawTier);
  const config = getPlanConfig(tier);
  if (storageUsedBytes + additionalBytes <= config.storageLimitBytes) return { ok: true };
  const required: RequiredPlan =
    tier === 'free' ? 'verified' : tier === 'verified' ? 'legacy' : 'legacy';
  return planDenied(
    'LIMIT_REACHED',
    `Storage limit reached (${Math.round(config.storageLimitBytes / (1024 * 1024))} MB on ${config.displayName}). Existing files remain available; upgrade or delete files you no longer need before uploading more.`,
    required
  );
}

export function tierFromProfile(
  profile: (BillingProfileFields & { subscription_tier?: string | null }) | null,
  billingIdentity?: BillingIdentityLink | null
): PlanTier {
  return resolveEffectivePlanTier(profile, billingIdentity);
}

export function planConfigForProfile(
  profile: { subscription_tier?: string | null } | null,
  billingIdentity?: BillingIdentityLink | null
): PlanConfig {
  return getPlanConfig(tierFromProfile(profile, billingIdentity));
}
